import { memo, useCallback, useMemo } from 'react';

import { type SegmentedControlOption } from '@air/component-segmented-control';

import { SegmentedControlRow } from '~/components/ShareModal/components/SegmentedControlRow';
import {
  DOWNLOAD_PERMISSION_OPTION_ALL_ASSETS,
  DOWNLOAD_PERMISSION_OPTION_NONE,
  DOWNLOAD_PERMISSION_OPTION_SINGLE_FILE,
  getDownloadPermissionMode,
} from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';

export type DownloadsRowProps = {
  /** Workspace-level capability — when false, "Single-file only" and "All assets" segments are disabled. */
  workspaceCanDownload: boolean;
  canViewOriginal: boolean;
  hideDownloadAll: boolean | null | undefined;
  isLoading: boolean;
  onChange: (next: { canViewOriginal: boolean; hideDownloadAll: boolean }) => void;
  'data-testid'?: string;
};

export const DownloadsRow = memo(
  ({
    workspaceCanDownload,
    canViewOriginal,
    hideDownloadAll,
    isLoading,
    onChange,
    'data-testid': dataTestId,
  }: DownloadsRowProps) => {
    const selectedValue = useMemo(
      () => getDownloadPermissionMode(canViewOriginal, hideDownloadAll),
      [canViewOriginal, hideDownloadAll],
    );

    const options = useMemo((): SegmentedControlOption[] => {
      const next: SegmentedControlOption[] = [{ ...DOWNLOAD_PERMISSION_OPTION_NONE, disabled: isLoading }];

      // Mirror CustomFieldsRow: if the workspace can't download but the link is currently
      // on a download option, include it disabled so the user sees the actual state rather
      // than a silently-shifted segment.
      const includeSingleFileForState = selectedValue === 'single-file' && !workspaceCanDownload;

      if (workspaceCanDownload || includeSingleFileForState) {
        next.push({
          ...DOWNLOAD_PERMISSION_OPTION_SINGLE_FILE,
          disabled: includeSingleFileForState ? true : isLoading,
        });
      }

      const includeAllAssetsForState = selectedValue === 'all-assets' && !workspaceCanDownload;

      if (workspaceCanDownload || includeAllAssetsForState) {
        next.push({
          ...DOWNLOAD_PERMISSION_OPTION_ALL_ASSETS,
          disabled: includeAllAssetsForState ? true : isLoading,
        });
      }

      return next;
    }, [isLoading, selectedValue, workspaceCanDownload]);

    const selectedOption = useMemo(
      () => options.find((option) => option.value === selectedValue) ?? options[0],
      [options, selectedValue],
    );

    const handleSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (option.value === 'none') {
          onChange({ canViewOriginal: false, hideDownloadAll: true });
        } else if (option.value === 'single-file') {
          onChange({ canViewOriginal: true, hideDownloadAll: true });
        } else if (option.value === 'all-assets') {
          onChange({ canViewOriginal: true, hideDownloadAll: false });
        }
      },
      [onChange],
    );

    return (
      <SegmentedControlRow
        data-testid={dataTestId}
        label="Allow downloading"
        onSelect={handleSelect}
        options={options}
        selectedOption={selectedOption}
      />
    );
  },
);

DownloadsRow.displayName = 'DownloadsRow';
